import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListingCardProps } from './types';
import BuyerCard from './BuyerCard';
import { buyerRoomsFloor } from './buyerRoomsFloor';
import ListingCardManageMenu from './ListingCardManageMenu';
import {
  buildPropertyChips,
  buildCardOpenProps,
  buildCardStateClass,
  connectHintText,
  budgetDisplay,
  roomsDisplay,
  sellerEstimateInfoText,
  sellerPriceDisplay,
  sizeDisplay,
} from './cardUtils';
import {
  useCompactOnWrap,
  useSameLine,
  useClampOverflow,
  useShortenOnOverflow,
} from './useCardOverflow';
import { formatStreetLine, formatAddressLine } from '../utils/addressFormatting';
import PhotoCarousel from '../common/PhotoCarousel/PhotoCarousel';
import MultiImageModal from '../ImageModal/MultiImageModal';
import ZoomIcon from '../BuildingModal/Comments/ZoomIcon';
import EstimateInfoTooltip from '../common/EstimateInfoTooltip/EstimateInfoTooltip';
import chatBubbleIcon from '../common/icons/chat-bubble.svg';
import shareIcon from '../common/icons/share.svg';
import sellerHouseGraphic from '../common/icons/house.png';
import buyerCoinGraphic from '../common/icons/coin.png';
import VerifiedChip from '../common/VerifiedShieldBadge/VerifiedChip';
import VerifiedShieldBadge from '../common/VerifiedShieldBadge/VerifiedShieldBadge';
import './ListingCard.scss';

const DEFAULT_NEW_THRESHOLD_DAYS = 7;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

// Brands a Jaakko-estimated price (estimating + completed estimate states),
// mirroring the avatar shown next to the estimate in the Add-my-place flow.
const JAAKKO_AVATAR = `${process.env.PUBLIC_URL}/assets/jaakko_avatar.png`;

/**
 * "New" is derived on the frontend from `updated_at` (no backend isNew field).
 * Guards against missing/invalid dates so a bad value never throws or wrongly
 * flags a listing as new.
 */
const isNewListing = (updatedAt: string | undefined, thresholdDays: number): boolean => {
  if (!updatedAt) return false;
  const updated = new Date(updatedAt).getTime();
  if (Number.isNaN(updated)) return false;
  return Date.now() - updated <= thresholdDays * MS_PER_DAY;
};

/** Buyer "looking for" indicator on the small card. */
const SearchGlyph = () => (
  <svg className="listing-card__search-glyph" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M5.66667 0C8.79628 0 11.3333 2.53705 11.3333 5.66667C11.3333 6.93085 10.9185 8.09772 10.2188 9.04037L12.5892 11.4108C12.9146 11.7362 12.9146 12.2638 12.5892 12.5892C12.2638 12.9146 11.7362 12.9146 11.4108 12.5892L9.04037 10.2188C8.09772 10.9185 6.93085 11.3333 5.66667 11.3333C2.53705 11.3333 0 8.79628 0 5.66667C0 2.53705 2.53705 0 5.66667 0ZM5.66667 1.33333C3.27343 1.33333 1.33333 3.27343 1.33333 5.66667C1.33333 8.0599 3.27343 10 5.66667 10C8.0599 10 10 8.0599 10 5.66667C10 3.27343 8.0599 1.33333 5.66667 1.33333Z" fill="#29363D" />
  </svg>
);

/**
 * Dynamic listing card. `type` (seller/buyer, derived from could_buy when not
 * passed) and `size` ('big' | 'small'; buyer also 'medium') pick the layout;
 * buyer delegates to BuyerCard. State is derived from which fields are present —
 * sparse listings render fewer rows.
 */
const ListingCard: React.FC<
  ListingCardProps & { onMouseEnter?: () => void; onMouseLeave?: () => void }
> = (props) => {
  const {
    listing,
    size = 'big',
    type,
    onOpen,
    isLiked = false,
    onToggleLike,
    couldBuyActive = false,
    onToggleCouldBuy,
    newThresholdDays = DEFAULT_NEW_THRESHOLD_DAYS,
    hideNewBadge = false,
    isLoggedIn,
    setLoginModalVisible,
    onEdit,
    onDelete,
    onShare,
    selected = false,
    hideEstimateAvatar = false,
    hideEmptyCompactTitle = false,
    compactHeadline = 'stats',
    priceText,
    priceTextShort,
    className,
    onMouseEnter,
    onMouseLeave,
  } = props;
  const { t } = useTranslation();

  const isOwnListing = !!listing.isUserOwnEvent;
  const stateClass = buildCardStateClass('listing-card', selected, isOwnListing);

  const resolvedType = type ?? (listing.could_buy && !listing.could_sell ? 'buyer' : 'seller');

  const [lightboxOpen, setLightboxOpen] = useState(false);
  // "Send message" opens the listing chat; the pill shows a spinner and blocks
  // repeat clicks until the handler settles.
  const [isCouldBuyLoading, setIsCouldBuyLoading] = useState(false);
  const isBuyer = resolvedType === 'buyer';
  // JAA-355: badges are viewer-specific; a no-reload logout flip (token expiry, logout in flight) must not keep them live.
  const showOpenChat = !!listing.conversation_id && isLoggedIn !== false;

  // Interactive (big) seller card overflow handling — shared with BuyerCard.
  // Stats ride one line with a dot when they fit (else wrap, dot off); a long
  // description gets a Show more/less toggle; extra tags fold into a "+N".
  const [statsRef, statsInline] = useSameLine<HTMLDivElement>(
    '.listing-card__stat',
    `${listing.property_size}|${listing.property_rooms}`,
  );
  const showStatDot = statsInline && !!listing.property_size && !!listing.property_rooms;
  const [descExpanded, setDescExpanded] = useState(false);
  const [descRef, descOverflows] = useClampOverflow<HTMLParagraphElement>(
    listing.property_description,
    descExpanded,
  );

  const manageNode =
    onEdit || onDelete ? (
      <ListingCardManageMenu
        onEdit={onEdit && (() => onEdit(listing))}
        onDelete={onDelete && (() => onDelete(listing))}
        onShare={onShare && (() => onShare(listing))}
        shareDisabledLabel={
          listing.moderation_hidden
            ? t('shareButton.notShareable', 'Not shareable')
            : undefined
        }
      />
    ) : null;

  const images = listing.images ?? [];

  const budgetLabel = budgetDisplay(listing);
  // The short form is the card's price everywhere it shares a line with other
  // controls: a full range is three grouped figures wide ("440 000-2 000 000 EUR")
  // and pushed Share and Suggest off the footer (JAA-350).
  const budgetLabelShort = budgetDisplay(listing, true);
  // Small card only: show the exact figure when the line has room for it and
  // fall back to thousands when it does not, rather than always shortening.
  const [priceRef, priceIsShort] = useShortenOnOverflow<HTMLSpanElement>(
    priceText ?? budgetLabel,
  );
  const sellerPrice = sellerPriceDisplay(listing, t);
  const messageSellerLabel = t('listingCard.messageSeller', 'Send message');
  const [messageSellerLabelRef, messageSellerLabelWrapped] =
    useCompactOnWrap<HTMLSpanElement>(
      `${messageSellerLabel}|${sellerPrice.kind}|${sellerPrice.text}|${
        isCouldBuyLoading ? 'loading' : 'idle'
      }|${onToggleCouldBuy ? 'visible' : 'hidden'}|${isOwnListing ? 'own' : 'other'}`,
    );

  if (isBuyer && size !== 'small') {
    return (
      <BuyerCard
        listing={listing}
        priceLabel={priceText ?? budgetLabelShort}
        size={size}
        isLoggedIn={isLoggedIn}
        setLoginModalVisible={setLoginModalVisible}
        isLiked={isLiked}
        onToggleLike={onToggleLike}
        onOpen={onOpen}
        selected={selected}
        className={className}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        manageNode={manageNode}
      />
    );
  }

  const isNew = !hideNewBadge && (listing.is_new ?? isNewListing(listing.updated_at, newThresholdDays));

  const chips = buildPropertyChips(listing, t);

  // A Jaakko-estimated price is branded with the Jaakko avatar. Compact building
  // previews may hide completed estimate avatars, but pending estimates always
  // keep the avatar because the placeholder itself is a Jaakko status.
  const renderEstimatePrice = (
    modifier: string,
    text: string,
    estimateKind: 'estimate' | 'estimating',
    forceAvatar = false,
  ) => (
    <span className="listing-card__price-wrap">
      <span className={`listing-card__price ${modifier}`}>{text}</span>
      {(forceAvatar || !hideEstimateAvatar) && (
        <EstimateInfoTooltip<HTMLButtonElement>
          ariaLabel={t('listingCard.estimateInfoAria', 'About this Jaakko estimate')}
          content={sellerEstimateInfoText(estimateKind, t)}
        >
          {(triggerProps) => (
            <button
              {...triggerProps}
              type="button"
              className="listing-card__price-avatar-trigger"
            >
              <img src={JAAKKO_AVATAR} alt="" aria-hidden="true" className="listing-card__price-avatar" />
            </button>
          )}
        </EstimateInfoTooltip>
      )}
    </span>
  );

  // Seller price cell, in priority order: user asking price (black) >
  // completed Jaakko estimate midpoint (branded) > "estimating price" while
  // pending > "No set price".
  const renderSellerPrice = () => {
    if (sellerPrice.kind === 'asking') {
      return <span className="listing-card__price">{sellerPrice.text}</span>;
    }
    if (sellerPrice.kind === 'estimate') {
      return renderEstimatePrice(
        'listing-card__price--estimate',
        sellerPrice.text,
        'estimate',
        false,
      );
    }
    if (sellerPrice.kind === 'estimating') {
      return renderEstimatePrice(
        'listing-card__price--estimating',
        sellerPrice.text,
        'estimating',
        true,
      );
    }
    return (
      <span className="listing-card__price listing-card__price--unset">
        {sellerPrice.text}
      </span>
    );
  };

  const streetLine = formatStreetLine(listing.address);
  const addressLine = formatAddressLine(listing.address);

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  // Standalone top-right Share button, shown when a share handler is given and
  // there's no manage menu (which carries its own share). Shared by both sizes.
  const shareButton =
    onShare && !manageNode ? (
      <button
        type="button"
        className="listing-card__share"
        disabled={listing.moderation_hidden}
        aria-label={
          listing.moderation_hidden
            ? t('shareButton.notShareable', 'Not shareable')
            : t('listingCard.share', 'Share')
        }
        onClick={(e) => {
          stop(e);
          if (!listing.moderation_hidden) onShare(listing);
        }}
      >
        <img
          className="listing-card__share-icon"
          src={shareIcon}
          alt=""
          aria-hidden="true"
        />
      </button>
    ) : null;

  const carouselLabels = {
    prev: t('listingCard.prevPhoto', 'Previous photo'),
    next: t('listingCard.nextPhoto', 'Next photo'),
    goTo: (index: number) => t('listingCard.goToPhoto', { index: index + 1 }),
  };

  const galleryImages = images.map((im) => ({
    url: im.url,
    thumbnailUrl: im.thumbnailUrl || im.url,
    metadata: { dimensions: { width: 0, height: 0 }, size_kb: 0 },
  }));

  // The big card's "view all photos" button opens the gallery lightbox; passed
  // to the carousel as a trailing control (the small card has no lightbox).
  const viewAllButton = size !== 'small' && images.length > 1 ? (
    <button
      type="button"
      className="listing-card__view-all"
      aria-label={t('listingCard.viewAllPhotos', 'View all photos')}
      onClick={(e) => {
        stop(e);
        setLightboxOpen(true);
      }}
    >
      <ZoomIcon color="#fff" size={18} />
    </button>
  ) : null;

  const lightbox =
    size !== 'small' && lightboxOpen ? (
      <MultiImageModal
        images={galleryImages}
        closeImageModal={() => setLightboxOpen(false)}
      />
    ) : null;

  const rootProps = buildCardOpenProps(listing, onOpen);

  // Gated on the same decision the row renders, not on the flat mirror. A buyer
  // who gave only a maximum size writes property_size_max and nothing else, so a
  // property_size test hid the whole stats row and threw away the "≤ 60 m²" the
  // card had already computed.
  const { rooms: storedRooms, flexible: roomsFlexible } = roomsDisplay(listing);
  const acceptedRoomFloor = isBuyer ? buyerRoomsFloor(storedRooms ?? NaN, roomsFlexible) : NaN;
  const rooms = isBuyer
    ? Number.isFinite(acceptedRoomFloor) && acceptedRoomFloor >= 1 ? `${acceptedRoomFloor}+` : undefined
    : storedRooms;
  const hasStats = sizeDisplay(listing).kind !== 'none' || !!rooms;

  const statsRow = (
    <div className="listing-card__stats" ref={statsRef}>
      {!!listing.property_size && (
        <span
          className={`listing-card__stat${showStatDot ? ' listing-card__stat--sep' : ''}`}
        >
          {listing.property_size}m²
        </span>
      )}
      {!!listing.property_rooms && (
        <span className="listing-card__stat">
          {listing.property_rooms} {t('listingCard.rooms')}
        </span>
      )}
    </div>
  );

  if (size === 'small') {
    // Same decision as the big buyer card, rendered flat: this card has no room
    // for a styled "+", and a seller's single size keeps its bare "m²".
    const sizeParts = sizeDisplay(listing);
    const smallSizeText = (() => {
      if (sizeParts.kind === 'range') return `${sizeParts.min}–${sizeParts.max} m²`;
      if (sizeParts.kind === 'min') return `${sizeParts.min}${isBuyer ? '+m²' : 'm²'}`;
      if (sizeParts.kind === 'max') return `≤ ${sizeParts.max} m²`;
      return '';
    })();
    const smallStats = (
      <div className="listing-card__stats listing-card__stats--wrap">
        {isBuyer && <SearchGlyph />}
        <span className="listing-card__stat-flow">
          {!!smallSizeText && (
            <span className="listing-card__stat">{smallSizeText}</span>
          )}
          {!!smallSizeText && !!rooms && (
            <span className="listing-card__stat-sep" aria-hidden="true" />
          )}
          {!!rooms && (
            <span
              className="listing-card__stat"
              title={
                roomsFlexible
                  ? t('newLandingPage.couldBuy.main.roomsFlexible')
                  : undefined
              }
            >
              {rooms} {t('listingCard.rooms')}
              {!isBuyer && roomsFlexible && ' ± 1'}
            </span>
          )}
        </span>
      </div>
    );
    const hideCompactTitle = hideEmptyCompactTitle && !hasStats && !listing.property_description;
    const expandEmptyBuyerPlaceholder = isBuyer && hideCompactTitle;
    // Lines the description may take are whatever the headline and the price
    // row leave in the 120px card. The seller always renders a price cell; a
    // buyer's budget (or a preformatted range) sits in the same slot and costs
    // the same line, so only a buyer with nothing below gets the third line.
    const hasPriceRow = !isBuyer || !!priceText || !!budgetLabel;
    const descriptionLines = expandEmptyBuyerPlaceholder
      ? hasPriceRow
        ? 3
        : 5
      : isBuyer && !hasPriceRow
        ? 3
        : 2;
    const smallDescriptionClass = `listing-card__description${
      descriptionLines !== 2 ? ` listing-card__description--${descriptionLines}line` : ''
    }`;

    return (
      <div
        className={`listing-card listing-card--small${stateClass}${isBuyer ? ' listing-card--buyer' : ''}${
          manageNode ? ' listing-card--has-manage' : ''
        }${listing.verified ? ' listing-card--verified' : ''}${className ? ` ${className}` : ''}`}
        {...rootProps}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {manageNode}
        {shareButton}
        {listing.verified && (
          <VerifiedShieldBadge
            label={t(isBuyer ? 'verification.verifiedBuyer' : 'verification.verifiedSeller')}
            sizePx={20}
            className="listing-card__verified-thumb"
          />
        )}
        <PhotoCarousel
          block="listing-card"
          images={isBuyer ? [] : images}
          activeAlt={addressLine}
          labels={carouselLabels}
          badge={
            isNew && !isBuyer ? (
              <span className="listing-card__badge">{t('listingCard.new')}</span>
            ) : null
          }
          emptyContent={
            <div className="listing-card__visual-empty" aria-hidden="true">
              <img
                className="listing-card__empty-graphic"
                src={isBuyer ? buyerCoinGraphic : sellerHouseGraphic}
                alt=""
              />
            </div>
          }
        />
        <div className="listing-card__compact">
          <div className="listing-card__compact-top">
            {hasStats && compactHeadline === 'stats' ? (
              smallStats
            ) : hideCompactTitle ? (
              null
            ) : (
              <div className="listing-card__compact-headline">
                {isBuyer && <SearchGlyph />}
                <p className="listing-card__compact-address">{streetLine}</p>
              </div>
            )}
            {/* Small card: no tags. The description is line-clamped with an
                ellipsis (see descriptionLines), else the connect prompt only
                for sparse listings. */}
            {listing.property_description ? (
              <p
                className={smallDescriptionClass}
              >
                {listing.property_description}
              </p>
            ) : !hasStats ? (
              <p className={smallDescriptionClass}>
                {connectHintText(t, isBuyer, isOwnListing)}
              </p>
            ) : null}
          </div>
          {priceText ? (
            <span ref={priceRef} className="listing-card__price">
              {priceIsShort && priceTextShort ? priceTextShort : priceText}
            </span>
          ) : (
            <>
              {/* A buyer's budget shows when they set one, but never the
                  "No set price" fallback: a seller who named no asking price is
                  worth flagging, a buyer who named no budget is just normal. */}
              {isBuyer && budgetLabel && (
                <span ref={priceRef} className="listing-card__price">
                  {priceIsShort ? budgetLabelShort : budgetLabel}
                </span>
              )}
              {/* Seller price routes through renderSellerPrice so the Jaakko
                  estimate fallback (completed / estimating states) still shows
                  when no asking price is set. */}
              {!isBuyer && renderSellerPrice()}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`listing-card${stateClass}${className ? ` ${className}` : ''}`}
      {...rootProps}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {manageNode}
      {shareButton}
      <PhotoCarousel
        block="listing-card"
        images={images}
        activeAlt={addressLine}
        labels={carouselLabels}
        badge={isNew ? <span className="listing-card__badge">{t('listingCard.new')}</span> : null}
        overlay={<div className="listing-card__visual-overlay" />}
        emptyContent={
          <div className="listing-card__visual-empty" aria-hidden="true">
            <img className="listing-card__empty-graphic" src={sellerHouseGraphic} alt="" />
          </div>
        }
        trailingControls={viewAllButton}
      />

      <div className="listing-card__content">
        <div className="listing-card__info">
          {/* With no stats the street becomes the headline below, so the small
              address line would duplicate it — only show it alongside stats. */}
          {hasStats && <p className="listing-card__address">{addressLine}</p>}

          <div className="listing-card__headline-group">
            {hasStats ? (
              statsRow
            ) : (
              <p className="listing-card__headline-address">{streetLine}</p>
            )}

            {listing.property_description && (
              <div className="listing-card__desc">
                <p
                  ref={descRef}
                  className={`listing-card__description${descExpanded ? ' listing-card__description--full' : ''
                    }`}
                >
                  {listing.property_description}
                </p>
                {descOverflows && (
                  <button
                    type="button"
                    className="listing-card__more-link"
                    onClick={(e) => {
                      stop(e);
                      setDescExpanded((v) => !v);
                    }}
                  >
                    {descExpanded
                      ? t('common.showLess', 'Show less')
                      : t('common.showMore', 'Show more')}
                  </button>
                )}
              </div>
            )}
          </div>

          {(listing.verified || chips.length > 0) && (
            <div className="listing-card__chips">
              {listing.verified && <VerifiedChip type={resolvedType} />}
              {chips.map((chip) => (
                <span className="listing-card__chip" key={chip}>
                  {chip}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="listing-card__footer">
          {resolvedType === 'seller' ? renderSellerPrice() : null}
          {/* Never offer "Send message" on the viewer's own listing: the backend
              rejects buying from yourself, which surfaced as a generic error
              alert. Gated here rather than per caller so every surface that
              renders a card (explore list, building page) is covered. An
              existing chat needs no toggle, so "Open chat" also shows where
              the caller withheld one (the building page while the viewer
              already sells there). */}
          {(onToggleCouldBuy || showOpenChat) && !isOwnListing && (
            <button
              type="button"
              className={`listing-card__could-buy${
                messageSellerLabelWrapped ? ' listing-card__could-buy--wrapped' : ''
              }${
                couldBuyActive ? ' listing-card__could-buy--active' : ''
              }${isCouldBuyLoading ? ' listing-card__could-buy--loading' : ''}`}
              aria-pressed={showOpenChat ? undefined : couldBuyActive}
              aria-busy={isCouldBuyLoading}
              disabled={isCouldBuyLoading}
              onClick={async (e) => {
                stop(e);
                if (isCouldBuyLoading) return;
                // JAA-355: the backend annotated this row with the viewer's
                // existing conversation, so the pill opens it directly. The
                // draft-chat path is skipped on purpose — it resolves only by
                // sellWillingnessId against a lazily-fetched list, which is
                // empty on first click and never matches legacy conversations.
                if (showOpenChat) {
                  window.dispatchEvent(
                    new CustomEvent('jaakko:open-conversation', {
                      detail: { conversationId: listing.conversation_id },
                    }),
                  );
                  return;
                }
                if (!onToggleCouldBuy) return;
                // Signed out no caller awaits anything: the draft chat opens at
                // once (JAA-325), so the spinner would only flash for a frame.
                if (isLoggedIn === false) {
                  onToggleCouldBuy(listing);
                  return;
                }
                setIsCouldBuyLoading(true);
                try {
                  await onToggleCouldBuy(listing);
                } finally {
                  setIsCouldBuyLoading(false);
                }
              }}
            >
              {isCouldBuyLoading ? (
                <span className="listing-card__could-buy-spinner" aria-hidden="true" />
              ) : (
                <img className="listing-card__could-buy-icon" src={chatBubbleIcon} alt="" aria-hidden="true" />
              )}
              <span ref={messageSellerLabelRef} className="listing-card__could-buy-label">
                {showOpenChat
                  ? t('listingCard.openChat', 'Open chat')
                  : messageSellerLabel}
              </span>
            </button>
          )}
        </div>
      </div>
      {lightbox}
    </div>
  );
};

export default ListingCard;
